import type { MediaType } from "@/components/forms/schemas/media";
import type { IHit } from "@/types/common";

/**
 * Mood event groups, as the admin API's `/api/event-groups` routes answer them. JSON is camelCase.
 * Source: backend `EventGroupDbDto` (`interestedIn`, `createdBy`, `updatedBy`, `createdAt`, `updatedAt`,
 * `externalKey`), `EventGroupWithEventsDto` (`{group, events}`) and `EventGroupRefDbDto` on each event.
 */
/** The most events one group may hold; the admin API refuses an add past it. */
export const MAX_EVENTS_PER_GROUP = 250;

/**
 * The group an event carries. Every member is nullable because the backend's `EventGroupRefDbDto`
 * is, so that a ref read back from a Typesense hit can come without a name or an id.
 */
export type EventGroupRef = {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
};

export type EventGroup = {
  id: string;
  name: string;
  slug: string;
  media: MediaType;
  about?: string | null;
  interestedIn: number;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  /** The Tina record id that made the group, set once on create. Null for a group a curator made. */
  externalKey?: string | null;
  /** How many events hold the group. Null until the backend has counted it. */
  memberCount?: number | null;
};

/**
 * A group as `GET /api/event-groups/explore` answers it (the Typesense document). Every member
 * beyond `id`, `name` and `updatedAt` can be missing, and the times are unix seconds, not ISO
 * strings. It carries no `createdBy` or `updatedBy`: the search copy leaves the curators' uids out.
 */
export type EventGroupDocument = {
  id: string;
  name: string;
  slug?: string | null;
  media?: MediaType | null;
  about?: string | null;
  interestedIn?: number | null;
  /** The number of events in the group, any status. Null until the backend has counted it. */
  memberCount?: number | null;
  externalKey?: string | null;
  /**
   * Not in the search copy today (it leaves the curators' uids out); the Created By column reads
   * it and shows the Tina badge or a dash until the backend indexes it.
   */
  createdBy?: string | null;
  /** True when `externalKey` is set: Typesense cannot filter on a missing field, so this is the filter. */
  hasExternalKey?: boolean | null;
  createdAt?: number | null;
  updatedAt: number;
};

/** One hit of the explore list, the way the default data provider returns it. */
export type EventGroupHit = IHit<EventGroupDocument>;

/** An event as the group read returns it: the fields the group pages show, not the whole `EventDbDto`. */
export type GroupEvent = {
  id: string;
  name: string;
  startDate: string;
  endDate?: string | null;
  timezone?: string;
  status?: string;
  venue?: { id: string; name: string } | null;
  media?: MediaType;
  group?: EventGroupRef | null;
};

export type EventGroupWithEvents = { group: EventGroup; events: GroupEvent[] };
